import { CrmTaskStatus } from '@plato/data/prisma';
import { DatePicker } from '../../../components/ui/date-picker.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { SalesOrderSearchRow } from '../../../server/types/search-types.ts';
import { SearchEntity } from '../../../server/types/search-types.ts';
import { EntitySearchCombobox } from '../../shared/components/form-items-with-fetch/entity-search-combobox.tsx';

import type { CrmTaskInputDTO } from '../../../server/dto/crm-task-input-dto.ts';
import { getDueDateColor } from '../../../utils/task-helpers.ts';

const salesOrderTypes = [SearchEntity.SalesOrder, SearchEntity.SalesQuote];

type TaskLinksInputProps = {
    value: CrmTaskInputDTO['links'];
    onChange: (value: CrmTaskInputDTO['links']) => void;
    taskId?: string;
};

export function TaskLinksInput({ value, onChange, taskId }: TaskLinksInputProps) {
    const { t } = useLocale('tasks');

    const salesOrderValue = value.find((link) => salesOrderTypes.includes(link.modelType as SearchEntity));

    const defaultValue = (salesOrderValue ? [salesOrderValue] : value).map((v) => {
        const entity = v.modelType as SearchEntity;

        return {
            id: v.modelId,
            entity,
            name: v.title || '',
        };
    });

    return (
        <EntitySearchCombobox
            entities={[SearchEntity.Customer, SearchEntity.SalesOrder, SearchEntity.SalesQuote]}
            inputPlaceholder={t('tasks__entity_search__placeholder')}
            className="w-full"
            variant="inline"
            tracking={{
                context: 'task_linked_entity_select',
                context_entity_type: 'task',
                context_entity_id: taskId,
            }}
            onValueChange={(values) => {
                const [item] = values;

                if (item) {
                    const newLinks = [
                        {
                            modelId: item.id,
                            modelType: item.entity,
                            title: item.name,
                        },
                    ];

                    if (salesOrderTypes.includes(item.entity)) {
                        const searchRow = item.original as SalesOrderSearchRow;

                        newLinks.push({
                            modelId: searchRow.customer_id,
                            modelType: SearchEntity.Customer,
                            title: searchRow.customer_name || '',
                        });
                    }

                    onChange(newLinks);
                } else {
                    onChange([]);
                }
            }}
            onClear={() => {
                onChange([]);
            }}
            defaultValue={defaultValue}
        />
    );
}

type DueDatePickerProps = {
    value: Date;
    onChange: (value: Date | undefined) => void;
    taskStatus?: CrmTaskStatus;
};

export function DueDatePicker({ value, onChange, taskStatus }: DueDatePickerProps) {
    const { t } = useLocale('tasks');

    return (
        <DatePicker
            title={t('tasks_overview_table__header__due_date')}
            disableIcon
            value={value}
            onChange={onChange}
            textColor={
                taskStatus === CrmTaskStatus.COMPLETED ? 'neutral-700' : getDueDateColor({ dueDate: value })
            }
            disabled={{
                before: new Date(),
            }}
        />
    );
}
